import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getCheckoutStatus } from "@/lib/billing";
import { CheckCircle, Spinner, Warning, ArrowRight } from "@phosphor-icons/react";

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "checking", payment_status: null, plan: null, error: null });
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session id" });
      return;
    }
    let attempts = 0;
    const tick = async () => {
      if (stopRef.current) return;
      attempts += 1;
      try {
        const r = await getCheckoutStatus(sessionId);
        if (r.payment_status === "paid") {
          setState({ phase: "paid", payment_status: r.payment_status, plan: r.plan });
          return;
        }
        if (r.status === "expired") {
          setState({ phase: "expired", payment_status: r.payment_status });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", payment_status: r.payment_status });
          return;
        }
        setState({ phase: "checking", payment_status: r.payment_status });
        setTimeout(tick, POLL_INTERVAL);
      } catch (e) {
        setState({ phase: "error", error: e?.response?.data?.detail || "Could not verify payment" });
      }
    };
    tick();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <div className="max-w-[680px] mx-auto px-6 py-16">
        <div className="border-2 border-ink-900 p-10 text-center" data-testid="billing-success-card">
          {state.phase === "paid" && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-hazard-safe mb-4">
                <CheckCircle size={22} weight="fill" className="text-white" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-safety mb-2">Payment confirmed</div>
              <h1 className="font-display font-black text-4xl tracking-tighter text-ink-900">You're on Team.</h1>
              <p className="mt-3 text-ink-700">Unlimited analyses, worker profiles, bulk upload, daily reports — unlocked.</p>
              {state.plan?.current_period_end && (
                <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
                  Plan active through {new Date(state.plan.current_period_end).toLocaleDateString()}
                </div>
              )}
              <Button data-testid="billing-go-dashboard" onClick={() => nav("/dashboard")} size="lg" className="mt-8 rounded-none bg-safety hover:bg-safety-hover text-white h-12 px-6">
                Open dashboard <ArrowRight size={14} className="ml-2" weight="bold" />
              </Button>
            </>
          )}
          {state.phase === "checking" && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-ink-900 mb-4">
                <Spinner size={22} weight="bold" className="text-white animate-spin" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Verifying with Stripe…</div>
              <h1 className="font-display font-black text-3xl tracking-tighter text-ink-900">Holding tight.</h1>
              <p className="mt-3 text-ink-700">Confirming your payment status.</p>
            </>
          )}
          {state.phase === "timeout" && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-hazard-warning mb-4">
                <Warning size={22} weight="bold" className="text-white" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-hazard-warning mb-2">Still pending</div>
              <h1 className="font-display font-black text-3xl tracking-tighter text-ink-900">Stripe is taking longer than expected.</h1>
              <p className="mt-3 text-ink-700">Check back later. If the plan didn't update, contact support at info@wsafeai.com.</p>
              <Button data-testid="billing-back-billing" onClick={() => nav("/dashboard/billing")} size="lg" variant="outline" className="mt-8 rounded-none border-ink-900">
                Back to billing
              </Button>
            </>
          )}
          {state.phase === "expired" && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-hazard-critical mb-4">
                <Warning size={22} weight="bold" className="text-white" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-hazard-critical mb-2">Session expired</div>
              <h1 className="font-display font-black text-3xl tracking-tighter text-ink-900">The checkout window timed out.</h1>
              <Button data-testid="billing-try-again" onClick={() => nav("/dashboard/billing")} size="lg" className="mt-8 rounded-none bg-safety hover:bg-safety-hover text-white">
                Try again
              </Button>
            </>
          )}
          {state.phase === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 bg-hazard-critical mb-4">
                <Warning size={22} weight="bold" className="text-white" />
              </div>
              <h1 className="font-display font-black text-3xl tracking-tighter text-ink-900">Couldn't verify payment</h1>
              <p className="mt-3 text-ink-700 break-words">{state.error}</p>
              <Button data-testid="billing-back-billing-err" onClick={() => nav("/dashboard/billing")} size="lg" variant="outline" className="mt-8 rounded-none border-ink-900">
                Back to billing
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
